import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CourseModeComponent } from '../course-mode/course-mode.component';
import { AiModeComponent } from '../ai-mode/ai-mode.component';
import { ManualModeComponent } from '../manual-mode/manual-mode.component';
import { AiQuizQuestion } from '@edtech-ui/core/models/quiz-creation/ai-quiz-generator';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { EditCourseTitleComponent } from '@edtech-ui/dashboard/manual-course-creation/edit-course-title/edit-course-title.component';
import { EditableStudyMaterialTypeEnum } from '@edtech-ui/constants/editable-material-type.enum';
import { MatDialog } from '@angular/material/dialog';
import { UpdateQuizNameTimePayload } from '@edtech-ui/core/models/quiz-review-interface.ts/get-all-quiz';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';
import { QuizMetadata } from '@edtech-ui/core/models/quiz-creation/quiz-meta-data';
import { AIQuizLimitExceededType } from '@edtech-ui/constants/quiz-limit-type.enum';
import { QuizTotalAiQuestionLimitDialogComponent } from '../quiz-total-ai-question-limit-dialog/quiz-total-ai-question-limit-dialog.component';

@Component({
	selector: 'app-create-quiz',
	standalone: true,
	imports: [FormsModule, RouterModule, CommonModule, ManualModeComponent, AiModeComponent, CourseModeComponent,MatProgressSpinnerModule],
	templateUrl: './create-quiz.component.html',
	styleUrl: './create-quiz.component.scss'
})
export class CreateQuizComponent implements OnInit, OnDestroy {

	selectedMode: 'manual' | 'ai' | 'course' = 'manual';

	selectMode(mode: 'manual' | 'ai' | 'course') {
		this.selectedMode = mode;
	}
	AIQuizLimitExceededType =AIQuizLimitExceededType;

	classroomId!: number;
	quizId!:string;
	_quizData: AiQuizQuestion[] = [];
	subscription = new Subscription();
	CourseStatusEnum = CourseStatusEnum;
	EditableStudyMaterialTypeEnum = EditableStudyMaterialTypeEnum;
	fullQuizData!: QuizMetadata;
	quizDataExists = false;
	isLoading = false;
	StudyMaterialTypeEnum =StudyMaterialTypeEnum;

	constructor(
		private route: ActivatedRoute,
		public _quizService: QuizManagementService,
		private router: Router,
		private _toastr: ToastrService,
		private dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.classroomId = +params.get('classroomId')!;
			this.quizId = params.get('quizId')!;
		if (this.quizId) {
			this.getQuizMetaData();
		}
	});
		this.quizData();
	}

	goToClassroomDetailPage() {
		const userId = localStorage.getItem('user_id');
		if (userId) {
			localStorage.removeItem(`${userId}_ai_quiz_data`);
		}
		localStorage.removeItem('ai_quiz_data');
		this._quizService.saveQuizData.next(null);
		this._quizService.saveQuizReviewData.next(null);
		this.router.navigate(['teacher-dashboard/classroom-detail-page', this.classroomId, 'quizzes'])
	}

	postUpdateQuizNameTime(quizId: string, quizName: string, timeLimit?: number) {
		const payload: UpdateQuizNameTimePayload = {
			quiz_name: quizName,
			time_limit: timeLimit ?? this.fullQuizData?.time_limit
		}
		this.subscription.add(
			this._quizService.postUpdateQuizNameTime(quizId, payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.getQuizMetaData();
						this._quizService.quizName.next(quizName);
					} else {
						this._toastr.error(response.Message)
					}
				}),
				catchError(() => {
					this._toastr.error('something went wrong while updating quiz name');
					return of(null);
				}),
			).subscribe()
		);
	}


	openEditTitleBox(showTimer: boolean = false): void {
		if (!this.fullQuizData) return;

		const dialogRef = this.dialog.open(EditCourseTitleComponent, {
			width: '500px',
			data: {
				materialEditId: this.fullQuizData.quiz_id,
				editTitle: this.fullQuizData.quiz_name,
				label: EditableStudyMaterialTypeEnum.Quiz,
				timeLimit: this.fullQuizData.time_limit,
				showTimer
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result) return;
			if (typeof result === 'string') {
				this.postUpdateQuizNameTime(this.fullQuizData.quiz_id, result);
			} else if (result.quiz_name || result.time_limit !== undefined) {
				this.postUpdateQuizNameTime( this.fullQuizData.quiz_id,result.quiz_name || 
					this.fullQuizData.quiz_name, result.time_limit
				);
			}
		});
	}

	goToQuizPage() {
		const userId = localStorage.getItem('user_id');
		if (userId) {
			localStorage.removeItem(`${userId}_ai_quiz_data`);
		}
		this.router.navigate(['teacher-dashboard/quiz-management', this.classroomId, this.quizId]);
	}

	quizData() {
		this._quizService.saveQuizData.subscribe(data => {
			this.quizDataExists = !!data && data.questions.length > 0;
		});
	}

	formatTime(totalSeconds: number | null): string {
		if (!totalSeconds) return '00:00:00';

		const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
		const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
		const seconds = (totalSeconds % 60).toString().padStart(2, '0');

		return `${hours}:${minutes}:${seconds}`;
	}

	getQuizMetaData() {
		this.isLoading = true;
		if (this.quizId) {
			this.subscription.add(
				this._quizService.getQuizMetaData(this.quizId).subscribe({
					next: (response) => {
						if (response.Success && response.Data) {
							this.isLoading = false;
							this.fullQuizData = response.Data;
							if (this.fullQuizData.total_questions > 0) {
								this.quizDataExists = true;
							} else {
								this.quizDataExists = false;
							}
						}
					},
					error:(err)=>{
					this.isLoading = false;      
					this._toastr.error('Failed to load quiz. Please try again.');
					}
				}));
  }
	}

	openQuizAiLimitDialogBox(limitType: AIQuizLimitExceededType, message?: string) {
		const dialogRef = this.dialog.open(QuizTotalAiQuestionLimitDialogComponent, {
			width: '450px',
			disableClose: true,
			data: { type: limitType, message },
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result?.goToManual) {
				this.selectMode('manual');
			}
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}